import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { Spinner, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'

interface SignerEntry {
  email: string
  name?: string
}

interface EnvelopeRow {
  id: number
  envelope_id: string
  status: string
  signer_email: string
  signer_name?: string
  sent_at: string
  created_by_id: number | null
  created_by_username: string | null
  client_signers: SignerEntry[]
  company_signers: SignerEntry[]
}

interface ConnectLogRow {
  id: number
  received_at: string
  envelope_id: string
  event: string
  status_from_payload: string
  previous_status: string
  new_status: string
  matched: boolean
}

interface DocumentESignAuditProps {
  documentId: number
}

const EVENT_LABELS: Record<string, string> = {
  'envelope-sent': 'Envelope sent',
  'envelope-delivered': 'Envelope delivered',
  'envelope-completed': 'Envelope signed/completed',
  'envelope-declined': 'Envelope declined',
  'envelope-voided': 'Envelope voided',
  'envelope-corrected': 'Envelope corrected',
  'envelope-resent': 'Envelope resent',
  'envelope-purge': 'Envelope purge',
  'envelope-deleted': 'Envelope deleted',
  'envelope-discard': 'Envelope discard',
  'envelope-created': 'Envelope created',
  'envelope-removed': 'Envelope removed',
  'envelope-reminder-sent': 'Envelope reminder sent',
  'recipient-sent': 'Recipient sent',
  'recipient-auto-responded': 'Recipient auto responded',
  'recipient-delivered': 'Recipient delivered',
  'recipient-completed': 'Recipient signed/completed',
  'recipient-declined': 'Recipient declined',
  'recipient-authentication-failure': 'Recipient authentication failure',
  'recipient-resent': 'Recipient resent',
  'recipient-delegate': 'Recipient delegate',
  'recipient-reassign': 'Recipient reassign',
  'recipient-finish-later': 'Recipient finish later',
  'envelope-signed': 'Envelope signed/completed',
  'recipient-signed': 'Recipient signed/completed',
}

function getEventLabel(event: string, t: (key: string, opts?: { defaultValue?: string }) => string): string {
  if (!event) return '—'
  const key = `docusign.audit.events.${event.replace(/-/g, '_')}`
  const def = EVENT_LABELS[event] || event
  return t(key, { defaultValue: def })
}

function getEnvelopeStatusClass(status: string): string {
  if (!status) return ''
  const s = status.toLowerCase()
  if (s === 'completed') return 'text-success fw-medium'
  if (s === 'declined' || s === 'voided') return 'text-danger'
  if (s === 'sent' || s === 'delivered' || s === 'recipient-sent') return 'text-primary'
  return ''
}

function formatSigners(signers: SignerEntry[]): string {
  if (!signers?.length) return '—'
  return signers.map((s) => (s.name?.trim() ? `${s.name} (${s.email})` : s.email)).join(', ')
}

const DocumentESignAudit: React.FC<DocumentESignAuditProps> = ({ documentId }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [envelopes, setEnvelopes] = useState<EnvelopeRow[]>([])
  const [connectLog, setConnectLog] = useState<ConnectLogRow[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([documentService.getDocusignEnvelopes(documentId).catch(() => []), documentService.getDocusignConnectLog(documentId).catch(() => [])])
      .then(([envList, logList]) => {
        if (!cancelled) {
          setEnvelopes(Array.isArray(envList) ? envList : [])
          setConnectLog(Array.isArray(logList) ? logList : [])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [documentId])

  if (loading) {
    return (
      <div className="d-flex">
        <Spinner animation="border" size="sm" role="status" />
      </div>
    )
  }

  return (
    <div className="document-esign-audit">
      <h6 className="mb-2">{t('docusign.envelopes.section')}</h6>
      {envelopes.length === 0 ? (
        <p className="text-muted small">{t('no_entries_found')}</p>
      ) : (
        <Table borderless size="sm" className="mb-4 document-esign-audit__envelopes" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: '9%' }}>{t('status')}</th>
              <th style={{ width: '12%' }}>{t('docusign.envelopes.sentBy')}</th>
              <th className="text-truncate" style={{ width: '12%' }}>
                {t('docusign.envelopes.clientSigners')}
              </th>
              <th className="text-truncate" style={{ width: '12%' }}>
                {t('docusign.envelopes.companySigners')}
              </th>
              <th style={{ width: '18%' }}>{t('docusign.envelopes.sentAt')}</th>
            </tr>
          </thead>
          <tbody>
            {envelopes.map((row) => (
              <tr key={row.id}>
                <td className={getEnvelopeStatusClass(row.status)}>{row.status || '—'}</td>
                <td className="text-truncate" title={row.created_by_username ?? '—'}>
                  {row.created_by_username ?? '—'}
                </td>
                <td className="small text-truncate" title={formatSigners(row.client_signers ?? [])}>
                  {formatSigners(row.client_signers ?? [])}
                </td>
                <td className="small text-truncate" title={formatSigners(row.company_signers ?? [])}>
                  {formatSigners(row.company_signers ?? [])}
                </td>
                <td className="text-truncate" title={row.sent_at ? format(new Date(row.sent_at), 'PPp') : '—'}>
                  {row.sent_at ? format(new Date(row.sent_at), 'PPp') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <h6 className="mb-2">{t('docusign.audit.section')}</h6>
      <p className="text-muted small mb-2">
        {t('docusign.audit.help')}
      </p>
      {connectLog.length === 0 ? (
        <p className="text-muted small">{t('no_entries_found')}</p>
      ) : (
        <Table borderless size="sm" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: '24%' }}>{t('docusign.audit.receivedAt')}</th>
              <th style={{ width: '14%' }}>{t('docusign.envelopes.envelopeId')}</th>
              <th>{t('docusign.audit.event')}</th>
              <th>{t('docusign.audit.previousStatus')}</th>
              <th style={{ width: '14%' }}>{t('docusign.audit.newStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {connectLog.map((row) => (
              <tr key={row.id}>
                <td className="small text-truncate" title={row.received_at ? format(new Date(row.received_at), 'PPp') : '—'}>
                  {row.received_at ? format(new Date(row.received_at), 'PPp') : '—'}
                </td>
                <td className="font-monospace small text-truncate" title={row.envelope_id || '—'}>
                  {row.envelope_id || '—'}
                </td>
                <td style={{ width: '24%' }}>{getEventLabel(row.event, t)}</td>
                <td style={{ width: '14%' }}>{row.previous_status || '—'}</td>
                <td style={{ width: '14%' }}>{row.new_status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

export default DocumentESignAudit
